import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { ReturnTypeEnum } from "../return";

// Product Criteria Type enum
export const ProductCriteriaTypeSchema = z.enum(["collections", "tags"]);

// Base product criteria
const BaseProductCriteriaSchema = z.object({ type: ProductCriteriaTypeSchema });

// Collections product criteria
const CollectionsProductCriteriaSchema = BaseProductCriteriaSchema.extend({
  type: z.literal("collections"),
  collections: z.array(z.string()),
});

// Product tags criteria
const ProductTagsProductCriteriaSchema = BaseProductCriteriaSchema.extend({
  type: z.literal("tags"),
  productTags: z.array(z.string()),
});

// Discriminated union for product criteria
export const ProductCriteriaSchema = z.discriminatedUnion("type", [
  CollectionsProductCriteriaSchema,
  ProductTagsProductCriteriaSchema,
]);

// Return Assessment schema
export const ReturnAssessmentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  kind: z.enum(ReturnTypeEnum),
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Return Assessment Groups schema
export const ReturnAssessmentGroupsSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  kind: z.enum(ReturnTypeEnum),
  productCriteria: ProductCriteriaSchema,
  assessmentIds: z.array(zExt.objectId()).nullable().optional(),
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Field Types enum
export enum FieldType {
  SHORT_TEXT = "short_text",
  LONG_TEXT = "long_text",
  SELECT = "select",
  NUMBER = "number",
  BOOLEAN = "boolean",
  FILE_UPLOAD = "file_upload",
  DATE = "date",
}

export const FieldTypeSchema = z.enum(FieldType);

// Base field schema
const BaseFieldSchema = z.object({
  type: FieldTypeSchema,
  label: z.string(),
  required: z.boolean().default(false),
});

// Short text field
export const ShortTextFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.SHORT_TEXT),
  config: z
    .object({
      placeholder: z.string().optional(),
      maxLength: z.number().optional(),
      minLength: z.number().optional(),
      pattern: z.string().optional(),
      patternMessage: z.string().optional(),
    })
    .optional(),
});

// Long text field
export const LongTextFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.LONG_TEXT),
  config: z
    .object({
      placeholder: z.string().optional(),
      maxLength: z.number().optional(),
      minLength: z.number().optional(),
      pattern: z.string().optional(),
      patternMessage: z.string().optional(),
    })
    .optional(),
});

// Select field
export const SelectFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.SELECT),
  config: z.object({
    options: z.array(z.object({ value: z.string(), label: z.string() })),
    maxSelections: z.number().optional(),
    minSelections: z.number().optional(),
  }),
});

// Number field
export const NumberFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.NUMBER),
  config: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      step: z.number().optional(),
      unit: z.string().optional(),
    })
    .optional(),
});

// Boolean field
export const BooleanFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.BOOLEAN),
  config: z
    .object({
      trueLabel: z.string().optional(),
      falseLabel: z.string().optional(),
    })
    .optional(),
});

// File upload field
export const FileUploadFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.FILE_UPLOAD),
  config: z
    .object({
      allowedFileTypes: z.array(z.string()).optional(),
      maxFileSize: z.number().optional(),
      maxFileCount: z.number().optional(),
    })
    .optional(),
});

// Date field
export const DateFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.DATE),
  config: z
    .object({
      minDate: z.date().optional(),
      maxDate: z.date().optional(),
      includeTime: z.boolean().optional(),
    })
    .optional(),
});

// Discriminated union for all field types
export const AssessmentFieldSchema = z.discriminatedUnion("type", [
  ShortTextFieldSchema,
  LongTextFieldSchema,
  SelectFieldSchema,
  NumberFieldSchema,
  BooleanFieldSchema,
  FileUploadFieldSchema,
  DateFieldSchema,
]);

// Return Assessment Definition schema
export const ReturnAssessmentDefinitionSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  assessmentId: zExt.objectId(),
  fields: z.array(AssessmentFieldSchema),
  iconName: z.string().optional(),
  iconColor: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Response schemas
const ShortTextResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.SHORT_TEXT),
  value: z.string(),
});

const LongTextResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.LONG_TEXT),
  value: z.string(),
});

const SelectResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.SELECT),
  value: z.array(z.string()),
});

const NumberResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.NUMBER),
  value: z.number(),
});

const BooleanResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.BOOLEAN),
  value: z.boolean(),
});

const FileUploadResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.FILE_UPLOAD),
  value: z.array(z.string()),
});

const DateResponseSchema = z.object({
  fieldId: z.string(),
  type: z.literal(FieldType.DATE),
  value: z.date().nullish(),
});

// Discriminated union for all response types
export const AssessmentResponseSchema = z.discriminatedUnion("type", [
  ShortTextResponseSchema,
  LongTextResponseSchema,
  SelectResponseSchema,
  NumberResponseSchema,
  BooleanResponseSchema,
  FileUploadResponseSchema,
  DateResponseSchema,
]);

// Product Assessment schema
export const ProductAssessmentSchema = z.object({
  assessmentDefinitionId: zExt.objectId(),
  assignee: zExt.objectId().nullable().optional(),
  responses: z.array(AssessmentResponseSchema),
  completedAt: z.date().nullish(),
});

// Enhanced Assessment schema combining assessment and definition
const ReturnAssessmentWithDefinitionSchema_ = ReturnAssessmentSchema.extend({
  definition: ReturnAssessmentDefinitionSchema.nullable(),
});

export const ReturnAssessmentWithDefinitionSchema: z.ZodType<ReturnAssessmentWithDefinition> =
  ReturnAssessmentWithDefinitionSchema_;
export interface ReturnAssessmentWithDefinition
  extends z.infer<typeof ReturnAssessmentWithDefinitionSchema_> {}

// Enhanced Assessment Groups schema with assessments and definitions included
const ReturnAssessmentGroupWithAssessmentsSchema_ =
  ReturnAssessmentGroupsSchema.extend({
    assessments: z.array(ReturnAssessmentWithDefinitionSchema).optional(),
  });

export const ReturnAssessmentGroupWithAssessmentsSchema: z.ZodType<ReturnAssessmentGroupWithAssessments> =
  ReturnAssessmentGroupWithAssessmentsSchema_;
export interface ReturnAssessmentGroupWithAssessments
  extends z.infer<typeof ReturnAssessmentGroupWithAssessmentsSchema_> {}

// Individual field types
export type ShortTextField = z.infer<typeof ShortTextFieldSchema>;
export type LongTextField = z.infer<typeof LongTextFieldSchema>;
export type SelectField = z.infer<typeof SelectFieldSchema>;
export type NumberField = z.infer<typeof NumberFieldSchema>;
export type BooleanField = z.infer<typeof BooleanFieldSchema>;
export type FileUploadField = z.infer<typeof FileUploadFieldSchema>;
export type DateField = z.infer<typeof DateFieldSchema>;

// Type exports
export type ReturnAssessment = z.infer<typeof ReturnAssessmentSchema>;
export type ReturnAssessmentGroups = z.infer<
  typeof ReturnAssessmentGroupsSchema
>;
export type ReturnAssessmentGroupsOmitId = Omit<ReturnAssessmentGroups, "_id">;
export type ReturnAssessmentDefinition = z.infer<
  typeof ReturnAssessmentDefinitionSchema
>;
export type AssessmentField = z.infer<typeof AssessmentFieldSchema>;
export type AssessmentResponse = z.infer<typeof AssessmentResponseSchema>;
export type ProductAssessment = z.infer<typeof ProductAssessmentSchema>;
export type ProductCriteria = z.infer<typeof ProductCriteriaSchema>;
